import { useId, useRef } from "react";
import type { ReactNode } from "react";
import { Icon } from "@common/index";
import { useDialogFocusTrap } from "@/hooks/useDialogFocusTrap";

interface AdminDrawerProps {
    // Names the dialog and heads the panel.
    title: string;
    onClose: () => void;
    children: ReactNode;
    // An edit drawer's save in flight: Escape and a backdrop click are
    // refused and focus parks on the panel, so the outcome has an open drawer
    // to land on.
    isProcessing?: boolean;
    // Pinned under the scrolling body (an edit drawer's Save and Cancel).
    footer?: ReactNode;
}

// The admin right-side drawer: Company History, Order History and Edit
// Organization today; the mockups draw Edit Location and Manage Categories
// the same way. Mounted only while open: the host renders it conditionally.
// The trap, scroll lock, focus in and back out, Escape and backdrop dismissal
// all come from useDialogFocusTrap.
export const AdminDrawer = ({
    title,
    onClose,
    children,
    isProcessing = false,
    footer,
}: AdminDrawerProps) => {
    const panelRef = useRef<HTMLDivElement>(null);
    const titleId = useId();
    const { backdropProps, panelProps } = useDialogFocusTrap({
        panelRef,
        isProcessing,
        onRequestClose: onClose,
    });

    return (
        <div
            className="fixed inset-0 z-50 flex justify-end bg-black/40"
            {...backdropProps}
        >
            <div
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                {...panelProps}
                className="flex h-full w-80 max-w-[85vw] flex-col bg-base-100 text-neutral shadow-xl focus:outline-none"
            >
                <div className="flex items-center justify-between gap-2 border-b border-base-300 px-4 py-3">
                    <h2 id={titleId} className="text-lg font-bold">
                        {title}
                    </h2>
                    {/* text-neutral: a ghost button on a light surface is
                        otherwise white on white (see the UI CLAUDE.md). */}
                    <button
                        type="button"
                        aria-label="Close"
                        onClick={onClose}
                        disabled={isProcessing}
                        className="btn btn-ghost btn-sm btn-square text-neutral"
                    >
                        <Icon name="close" className="text-2xl" />
                    </button>
                </div>
                <div className="flex-1 overflow-y-auto p-4">{children}</div>
                {footer && (
                    <div className="border-t border-base-300 px-4 py-3">
                        {footer}
                    </div>
                )}
            </div>
        </div>
    );
};
